import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import axios from 'axios';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
  MatPaginatorIntl,
} from '@angular/material/paginator';
import { pushPaginaAtiva, clearPaginas } from '../../../shared/activePage';
import { getPaginatorIntl } from '../../../shared/mat-paginator-intl';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { TooltipPosition, MatTooltipModule } from '@angular/material/tooltip';
import { EmpresasService } from '../../../services/empresa/empresas.service';
import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'app-listar-pessoa',
  standalone: true,
  imports: [
    MatTableModule,
    CommonModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    FormsModule,
    MatSortModule,
    MatTooltipModule,
  ],
  templateUrl: './listar-pessoas.html',
  styleUrl: './listar-pessoas.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: getPaginatorIntl,
    },
  ],
})
export class ListarPessoas implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  pessoas = new MatTableDataSource<any>();
  displayedColumns: string[] = [
    'nome',
    'tipoDoc',
    'numDoc',
    'cargo',
    'telefone',
    'empresa',
  ];

  empresas = new MatTableDataSource<any>();
  empresaFiltrada = '';

  fieldText = '';

  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;
  textoContagem = 'Mostrando 0 a 0 de 0 resultados';

  mustShowAddButton = signal(false);

  constructor(
    private readonly router: Router,
    private readonly empresaService: EmpresasService,
    private readonly pessoaService: PessoasService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    clearPaginas();
    pushPaginaAtiva({
            nome: 'Home',
            rota: '/home'
        });
    pushPaginaAtiva({
      nome: 'Pessoas',
      rota: ''
    });
    this.mustShowAddButton.set(
      this.authService.isMaster() ||
        this.authService.isGestor() ||
        this.authService.isSaude()
    );

    this.configurarFiltrosESorting();
    this.findAllPessoas();
    this.findAllEmpresas();
  }

  ngAfterViewInit(): void {
    this.configurarPaginatorESort();

    this.sort.active = 'nome';
    this.sort.direction = 'asc';
    this.pessoas.sort = this.sort;
  }

  private configurarFiltrosESorting() {
    this.pessoas.filterPredicate = (data: any) => {
      const textoFiltro = this.fieldText.toLowerCase();
      const passaFiltroTexto =
        !textoFiltro ||
        data.nomeCompleto?.toLowerCase().includes(textoFiltro) ||
        data.numeroDocumento?.toLowerCase().includes(textoFiltro);

      const passaFiltroEmpresa =
        !this.empresaFiltrada ||
        data.empresaNomeFantasia === this.empresaFiltrada;

      return passaFiltroTexto && passaFiltroEmpresa;
    };

    this.pessoas.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'id':
          return item.idPessoa;
        case 'nome':
          return item.nomeCompleto;
        case 'tipoDoc':
          return item.tipoDocumentoNome;
        case 'numDoc':
          return item.numeroDocumento;
        case 'cargo':
          return item.cargo;
        case 'telefone':
          return item.telefoneCelular;
        case 'empresa':
          return item.empresaNomeFantasia;
        default:
          return item[property];
      }
    };
  }

  private configurarPaginatorESort() {
    this.pessoas.paginator = this.paginator;
    this.pessoas.sort = this.sort;

    this.paginator.page.subscribe((event) => {
      this.pageSize = event.pageSize;
      this.atualizarContagem(event.pageIndex, event.pageSize);
    });
  }

  async findAllPessoas() {
    try {
      this.pessoas.data = await this.pessoaService.findAllPessoas();
      this.total = this.pessoas.data.length;
      this.atualizarContagem(0, this.pageSize);

      if (this.sort) {
        this.pessoas.sort = this.sort;
      }
    } catch (err) {
      this.total = 0;
      this.atualizarContagem(0, this.pageSize);
    }
  }

  async findAllEmpresas() {
    try {
      const response = await this.empresaService.findAllEmpresas();
      this.empresas.data = response;
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
    }
  }

  atualizarContagem(pageIndex: number, pageSize: number, totalRows?: number) {
    const totalExibicao = totalRows ?? this.pessoas.data.length;
    const maxPageIndex = Math.max(0, Math.ceil(totalExibicao / pageSize) - 1);
    pageIndex = Math.min(pageIndex, maxPageIndex);

    const startItem = pageIndex * pageSize;
    const endItem = startItem + pageSize;

    this.inicio = totalExibicao > 0 ? startItem + 1 : 0;
    this.fim = Math.min(endItem, totalExibicao);
    this.textoContagem = `Mostrando ${this.inicio} a ${this.fim} de ${totalExibicao} resultados`;

    if (totalExibicao === 0) {
      this.inicio = 0;
      this.fim = 0;
    }
  }

  updateFieldText(event: Event) {
    this.fieldText = (event.target as HTMLInputElement).value;
    this.aplicarFiltros();
  }

  filtrarPorEmpresa() {
    this.aplicarFiltros();
  }

  verDetalhes(id: number) {
    this.router.navigateByUrl('pessoas/' + id);
  }

  private aplicarFiltros() {
    const currentFilter = this.pessoas.filter;
    this.pessoas.filter = '';
    this.pessoas.filter = currentFilter || ' ';

    if (this.paginator) {
      this.paginator.firstPage();
    }

    this.atualizarContagem(0, this.pageSize, this.pessoas.filteredData.length);
  }

  adicionarPessoa() {
    this.router.navigate(['/pessoas/cadastrar']);
  }
}
